import pointInPolygon from 'point-in-polygon';
import {area, getCenter} from './utils/area';
import {simplify} from './utils/simplify';

const _pointInPolygon = (poly, point) => {
    return pointInPolygon(
        point.list(),
        poly.map((p) => p.list()),
    );
};

// ------------------------
class StatElem {
    constructor(scale, name) {
        this.scale = scale;
        this.name = name;
        this.visible = true;
        this.highlight = false;
        this.taxonomies = new Set();
    }

    renderText(ctx, x, y, text, angle = 0, fontSize = 16, highlight = false) {
        const existingLineWidth = ctx.lineWidth;
        ctx.font = `${fontSize}px sans-serif`;
        ctx.fillStyle = highlight ? 'rgb(0, 255, 0)' : '#fff';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 0.6;

        ctx.translate(x, y);
        ctx.rotate(angle);

        ctx.fillText(text, 0, 0);
        ctx.strokeText(text, 0, 0);

        ctx.rotate(-angle);
        ctx.translate(-x, -y);
        ctx.lineWidth = existingLineWidth;

        return ctx.measureText(text);
    }

    getTextBoundingBox(x, y, textSize, padding, angle = 0) {
        const textWidth = textSize.width;
        const textHeight =
            textSize.actualBoundingBoxAscent + textSize.actualBoundingBoxDescent;

        const box = [
            {x: 0 - padding, y: 0 - textSize.actualBoundingBoxAscent - padding},
            {
                x: textWidth + padding,
                y: 0 - textSize.actualBoundingBoxAscent - padding,
            },
            {
                x: textWidth + padding,
                y: 0 + textSize.actualBoundingBoxDescent + padding,
            },
            {x: 0 - padding, y: 0 + textSize.actualBoundingBoxDescent + padding},
        ];

        const rotatedBox = box.map((corner) => {
            const rotatedX = corner.x * Math.cos(angle) - corner.y * Math.sin(angle);
            const rotatedY = corner.x * Math.sin(angle) + corner.y * Math.cos(angle);
            return new Point(Math.round(rotatedX + x), Math.round(rotatedY + y), this.scale);
        });

        return rotatedBox;
    }
}

// ------------------------

// ------------------------
class Point extends StatElem {
    constructor(x, y, scale = null) {
        super(scale, 'point');
        this.x = x;
        this.y = y;
        this.radius = 3;

        Object.defineProperty(this, 'magnitude', {
            get: () => Math.sqrt(this.x ** 2 + this.y ** 2),
        });
        Object.defineProperty(this, 'inScaleBounds', {
            get: () =>
                this.scale &&
                0 <= this.x &&
                this.x < this.scale.x &&
                0 <= this.y &&
                this.y < this.scale.y,
        });
    }

    asScale(scale) {
        const ret = new Point(
            (this.x * scale.x) / this.scale.x,
            (this.y * scale.y) / this.scale.y,
            scale,
        );
        ret.highlight = this.highlight;
        return ret;
    }

    asPercent() {
        return this.asScale(new Point(1, 1));
    }

    static fromEvent(evt) {
        const event = evt.type.startsWith('touch') ? evt.touches[0] : evt;
        const bounds = evt.currentTarget.getBoundingClientRect();
        return new Point(
            event.clientX - bounds.left,
            event.clientY - bounds.top,
            new Point(bounds.width, bounds.height),
        );
    }

    meOrMyChild(elem) {
        return elem === this;
    }

    round() {
        this.x = Math.round(this.x);
        this.y = Math.round(this.y);
        return this;
    }

    copy() {
        return new Point(this.x, this.y, this.scale?.copy());
    }

    list() {
        return [this.x, this.y];
    }

    distance(other) {
        return Math.sqrt((this.x - other.x) ** 2 + (this.y - other.y) ** 2);
    }

    angle(other) {
        let ang = Math.atan2(this.y - other.y, this.x - other.x);
        return ang >= 0 ? ang : ang + 2 * Math.PI;
    }

    average(other) {
        return new Point((this.x + other.x) / 2, (this.y + other.y) / 2);
    }

    offset(other) {
        return new Point(this.x - other.x, this.y - other.y, this.scale);
    }

    move(other) {
        this.x += other.x;
        this.y += other.y;
    }

    contains(point) {
        return this.visible && this.distance(point) <= 4 * this.radius
            ? this
            : null;
    }

    render(ctx, colour) {
        ctx.fillStyle = this.highlight ? 'rgb(0, 255, 0)' : colour;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, 2 * Math.PI, false);
        ctx.fill();
    }

    drawLineTo(ctx, other, colour) {
        const prevWidth = ctx.lineWidth;
        ctx.strokeStyle = colour || 'rgb(255, 255, 0)';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(other.x, other.y);
        ctx.closePath();
        ctx.stroke();
        ctx.lineWidth = prevWidth;
    }
}

// ----------------------------------------
// ----------------------------------------
// ----- Crossable element to rule them all
class Curve extends StatElem {
    constructor(scale, name, p0, p1, cf, vari) {
        super(scale, name);
        this._p0 = p0;
        this._p1 = p1;
        this._cf = cf;
        this._vari = vari;

        // ------- defining properties
        Object.defineProperty(this, 'p0', {
            get: () => this._p0,
            set: (p) => (this._p0 = p),
            configurable: true,
        });
        Object.defineProperty(this, 'p1', {
            get: () => this._p1,
            set: (p) => (this._p1 = p),
            configurable: true,
        });
        Object.defineProperty(this, 'cf', {
            get: () => this._cf,
            set: (cf) => {
                this._cf = Math.abs(cf) < 0.15 ? 0 : cf;
            },
        });
        Object.defineProperty(this, 'vari', {
            get: () => this._vari,
            set: (vari) => {
                this._vari = Math.round(Math.abs(vari));
            },
        });
        this.vari = vari;
        this.cf = cf; // make sure cf gets updated

        // ------- helper properties
        Object.defineProperty(this, 'centre', {
            get: () => this.p0.average(this.p1),
        });
        Object.defineProperty(this, 'length', {
            get: () => this.p0.distance(this.p1),
        });
        Object.defineProperty(this, 'angle', {
            get: () => this.p0.angle(this.p1),
        });
        Object.defineProperty(this, 'is_arc', {
            get: () => Math.abs(this.cf) > 0,
        });
        Object.defineProperty(this, 'radius', {
            get: () => (0.5 * this.length) / Math.abs(this.cf),
        });
        Object.defineProperty(this, 'arc_params', {
            get: () => {
                let [arc_xa, arc_ya] = [
                    (this.p1.x - this.p0.x) / 2,
                    (this.p1.y - this.p0.y) / 2,
                ];
                let arc_a = Math.sqrt(arc_xa ** 2 + arc_ya ** 2);
                let arc_b = Math.sqrt(this.radius ** 2 - arc_a ** 2);
                let [arc_cx, arc_cy] = [this.p0.x + arc_xa, this.p0.y + arc_ya];
                let pc = new Point(
                    arc_cx - (Math.sign(this.cf) * arc_b * arc_ya) / arc_a,
                    arc_cy + (Math.sign(this.cf) * arc_b * arc_xa) / arc_a,
                );
                let pa = new Point(this.p0.angle(pc), this.p1.angle(pc));
                return {
                    cx: pc.x,
                    cy: pc.y,
                    a_min: (180 / Math.PI) * (this.cf > 0 ? pa.x : pa.y),
                    a_max: (180 / Math.PI) * (this.cf > 0 ? pa.y : pa.x),
                    r: this.radius,
                    var: this.vari,
                    inverted: this.cf < 0,
                };
            },
        });
        Object.defineProperty(this, 'line_params', {
            get: () => {
                return {
                    x0: this.p0.x,
                    y0: this.p0.y,
                    x1: this.p1.x,
                    y1: this.p1.y,
                    var: this.vari,
                    inverted:
                        this.p0.x > this.p1.x
                            ? false
                            : this.p0.x < this.p1.x
                                ? true
                                : this.p0.y > this.p1.y,
                };
            },
        });
    }

    copy(name) {
        const ret = new Curve(
            this.scale,
            name,
            this.p0.copy(),
            this.p1.copy(),
            this.cf,
            this.vari,
        );
        ret.move(new Point(10, 10));
        return ret;
    }

    asScale(scale) {
        const ret = new Curve(
            scale,
            this.name,
            this.p0.asScale(scale),
            this.p1.asScale(scale),
            this.cf,
            (this.vari * scale.magnitude) / this.scale.magnitude,
        );
        ret.highlight = this.highlight;
        return ret;
    }

    move(p) {
        this._p0.move(p);
        this._p1.move(p);
    }

    flip() {
        let [_p0, _p1] = [this.p1, this.p0];
        this.p0 = _p0;
        this.p1 = _p1;
        this.cf = -1 * this.cf;
    }

    contains(point, points = false) {
        return !this.visible
            ? null
            : points
                ? this.p0.contains(point) ?? this.p1.contains(point) ?? null
                : this.centre.contains(point)
                    ? this
                    : null;
    }

    remove(point) {
        // cannot remove points from curves
    }

    meOrMyChild(elem, me = true) {
        return (
            (me && elem === this) ||
            this.p0.meOrMyChild(elem) ||
            this.p1.meOrMyChild(elem)
        );
    }

    static fromApi({scale, name, ...params}) {
        if (
            ['cx', 'cy', 'a_min', 'a_max', 'r'].every((p) => params[p] !== undefined)
        ) {
            // we are constructing from an arc
            const {cx, cy, a_min, a_max, r} = params;
            const p0 = new Point(
                cx + r * Math.cos((a_min * Math.PI) / 180),
                cy + r * Math.sin((a_min * Math.PI) / 180),
                scale,
            );
            const p1 = new Point(
                cx + r * Math.cos((a_max * Math.PI) / 180),
                cy + r * Math.sin((a_max * Math.PI) / 180),
                scale,
            );
            return new Curve(
                scale,
                name,
                params.inverted ? p1 : p0,
                params.inverted ? p0 : p1,
                Math.min((0.5 * p0.distance(p1)) / params.r, 1) *
                (params.inverted ? -1 : 1),
                params.var || 0,
            );
        } else if (['x0', 'y0', 'x1', 'y1'].every((p) => params[p] !== undefined)) {
            // we are constructing from a line
            // because devices care only about the slope and not the order of the points,
            // but we do care about the order of the points here,
            // we take p0 to be the right most point or if x is equal we take the one on top
            // we then swap them if params.inverted is set to true
            // this accounts for when the line is vertical as the devices do it is considered a positive
            // except of course when it's inverted, then a vertical line is a negative.
            const {x0, y0, x1, y1} = params;
            const _pa = new Point(x0, y0, scale);
            const _pb = new Point(x1, y1, scale);
            const [p0, p1] =
                _pa.x > _pb.x || (_pa.x === _pb.x && _pa.y < _pb.y)
                    ? [_pa, _pb]
                    : [_pb, _pa];
            return new Curve(
                scale,
                name,
                params.inverted ? p1 : p0,
                params.inverted ? p0 : p1,
                0,
                params.var || 0,
            );
        } else {
            // don't know what we're constructing
            throw new Error('Invalid parameters for Curve');
        }
    }

    toApi(arc = false) {
        this.p0.round();
        this.p1.round();
        return arc ? this.arc_params : this.line_params;
    }

    render(ctx) {
        // useful vars
        let ang = this.angle + Math.PI / 2;
        let centre = this.centre;

        if (this.is_arc) {
            // useful vars
            let {cx, cy, a_min, a_max} = this.arc_params;
            let [ang0, ang1] = [(a_min * Math.PI) / 180, (a_max * Math.PI) / 180];

            // render arc
            ctx.strokeStyle = this.highlight ? 'rgb(0, 255, 0)' : 'rgb(255, 0, 0)';
            ctx.beginPath();
            ctx.arc(cx, cy, this.radius, ang0, ang1, false);
            ctx.closePath();
            ctx.stroke();

            // render var lines
            ctx.strokeStyle = this.highlight ? 'rgb(0, 255, 0)' : 'rgb(0, 255, 0)';
            ctx.beginPath();
            ctx.arc(cx, cy, this.radius + this.vari, ang0, ang1, false);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(cx, cy, this.radius - this.vari, ang0, ang1, false);
            ctx.stroke();
        } else {
            // render line
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = this.highlight ? 'rgb(0, 255, 0)' : 'rgb(255, 0, 0)';
            ctx.beginPath();
            ctx.moveTo(this.p0.x, this.p0.y);
            ctx.lineTo(this.p1.x, this.p1.y);
            ctx.closePath();
            ctx.stroke();

            // render var lines
            // sin and cos are changed because we rotate pi/2
            const [x_offset, y_offset] = [
                this.vari * Math.sin(this.angle),
                this.vari * -Math.cos(this.angle),
            ];
            ctx.strokeStyle = this.highlight ? 'rgb(0, 255, 0)' : 'rgb(0, 255, 0)';
            ctx.beginPath();
            ctx.moveTo(this.p0.x + x_offset, this.p0.y + y_offset);
            ctx.lineTo(this.p1.x + x_offset, this.p1.y + y_offset);
            ctx.closePath();
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(this.p0.x - x_offset, this.p0.y - y_offset);
            ctx.lineTo(this.p1.x - x_offset, this.p1.y - y_offset);
            ctx.closePath();
            ctx.stroke();
        }

        // render direction triangle
        let t_size = Math.min(Math.max(this.length / 10, 10), 20);
        ctx.strokeStyle = this.highlight ? 'rgb(0, 255, 0)' : 'rgba(0, 0, 0, 0.25)';
        ctx.fillStyle = this.highlight
            ? 'rgb(0, 255, 0)'
            : 'rgba(255, 255, 255, 1)';
        ctx.translate(centre.x, centre.y);
        // // sum -pi/2 if angle is between (pi/2, 3pi/2) and by pi/2 elsewhere i.e. sign of the cosine times pi/2
        // ctx.rotate(this.angle + Math.sign(Math.cos(this.angle)) * Math.PI/2 + (this.inverted? Math.PI : 0))
        ctx.rotate(ang);
        ctx.beginPath();
        ctx.moveTo(0, -t_size);
        ctx.lineTo(t_size, 0);
        ctx.lineTo(0, t_size);
        ctx.lineTo(0, -t_size);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.setTransform(1, 0, 0, 1, 0, 0); // reset translation and rotation

        // render points
        this.p0.render(
            ctx,
            this.highlight ? 'rgb(0, 255, 0)' : 'rgb(255, 255, 255)',
        );
        this.p1.render(
            ctx,
            this.highlight ? 'rgb(0, 255, 0)' : 'rgb(255, 255, 255)',
        );
    }

    renderLabel(ctx) {
        const name = ` ${this.name}`;
        const fontSize = 18;
        const angleOfLine = Math.atan2(
            this.p1.y - this.p0.y,
            this.p1.x - this.p0.x,
        );
        let degrees = (angleOfLine * 180) / Math.PI + 90;
        let x = this.p0.x;
        let y = this.p0.y;

        // Adjust for orientation
        const needsAdjustment = degrees >= 180 || degrees < 0;
        if (needsAdjustment) {
            degrees += degrees >= 180 ? -180 : 180;
            if (this.p0.x > this.p1.x) {
                x = this.p1.x;
                y = this.p1.y;
            }
        }

        const rotationRadians = ((degrees - 90) * Math.PI) / 180;
        const textSize = this.renderText(
            ctx,
            x,
            y,
            name,
            rotationRadians,
            fontSize,
            this.highlight,
        );

        return {
            id: this.name,
            boundsPolygon: this.getTextBoundingBox(x, y, textSize, 5, rotationRadians),
        };
    }
}

// ------------------------
class Area extends StatElem {
    constructor(scale, name, inverted, poly, ignore) {
        super(scale, name);
        this.poly = poly;
        this.ignore = ignore;
        this.inverted = inverted;
    }

    asScale(scale) {
        const ret = new Area(
            scale,
            this.name,
            this.inverted,
            this.poly.map((p) => p.asScale(scale)),
            this.ignore,
        );
        ret.highlight = this.highlight;
        return ret;
    }

    static fromApi({scale, name, inverted, poly, ignore}) {
        return new Area(
            scale,
            name,
            inverted,
            poly.map((p) => new Point(p[0], p[1], scale)),
            ignore,
        );
    }

    toApi() {
        return {
            color: [0, 0, 0],
            poly: this.poly.map((p) => p.round().list()),
        };
    }

    copy(name) {
        const ret = new Area(
            this.scale,
            name,
            this.inverted,
            this.poly.map((p) => p.copy()),
            this.ignore,
        );
        ret.move(new Point(10, 10));
        return ret;
    }

    move(p) {
        this.poly.forEach((tp) => tp.move(p));
    }

    addPoint(point = null) {
        let atIndex = this.poly.length;
        let toInsert = this.poly[this.poly.length - 1].average(this.poly[0]);
        if (!!point) {
            const dists = this.poly.map((p, i, a) =>
                p.average(a[i + 1] ?? a[0]).distance(point),
            );
            atIndex = dists.indexOf(Math.min(...dists)) + 1;
            toInsert = point.copy();
        }
        toInsert.scale = this.scale;
        this.poly.splice(atIndex, 0, toInsert);
        return toInsert;
    }

    contains(point, points = false) {
        if (!this.visible) return null;
        else if (points) {
            for (const p of this.poly) {
                let contains = p.contains(point);
                if (!!contains) return contains;
            }
            return null;
        } else return _pointInPolygon(this.poly, point) ? this : null;
    }

    remove(point) {
        if (this.poly.length > 3) {
            this.poly = this.poly.filter((p) => p !== point);
        }
    }

    meOrMyChild(elem, me = true) {
        return (me && elem === this) || this.poly.some((p) => p.meOrMyChild(elem));
    }

    render(ctx) {
        // useful vars
        ctx.strokeStyle = this.highlight
            ? 'rgb(0, 255, 0)'
            : this.ignore
                ? 'rgb(255, 255, 0)'
                : 'rgb(0, 0, 255)';
        ctx.fillStyle = this.highlight
            ? 'rgba(0, 255, 0, 0.05)'
            : this.ignore
                ? 'rgba(255, 255, 0, 0.05)'
                : 'rgba(0, 0, 255, 0.05)';

        // draw polygon
        ctx.beginPath();
        ctx.moveTo(this.poly[0].x, this.poly[0].y);
        this.poly.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.stroke();
        ctx.fill();

        // draw points
        this.poly.forEach((p) => {
            p.render(
                ctx,
                this.highlight
                    ? 'rgb(0, 255, 0)'
                    : this.ignore
                        ? 'rgb(255, 255, 0)'
                        : 'rgb(0, 0, 255)',
            );
        });
    }

    renderLabel(ctx) {
        const name = this.name;
        const center = getCenter(this.poly);
        const fontSize = 18;

        const textSize = this.renderText(
            ctx,
            center.x - ctx.measureText(name).width / 2,
            center.y - fontSize / 2,
            name,
            0,
            fontSize,
            this.highlight,
        );

        return {
            id: this.name,
            boundsPolygon: this.getTextBoundingBox(
                center.x - textSize.width / 2,
                center.y - fontSize / 2,
                textSize,
                5,
                0,
            ),
        };
    }
}

// -----------------------------------------------------
// -----------------------------------------------------
// -----------------------------------------------------
class StatElems {
    constructor(scale, elems) {
        this.scale = scale;
        this.elems = elems;
        this.changing = false; // just to signal components if they should freeze the stat elems before changing

        Object.defineProperty(this, 'length', {
            get: () => this.elems.length,
        });
        Object.defineProperty(this, 'curves', {
            get: () =>
                this.elems
                    .filter((elem) => Object.getPrototypeOf(elem) === Curve.prototype)
                    .sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase()),
        });
        Object.defineProperty(this, 'areas', {
            get: () =>
                this.elems
                    .filter((elem) => Object.getPrototypeOf(elem) === Area.prototype)
                    .sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase()),
        });
    }

    asScale(scale) {
        return new StatElems(
            scale,
            this.elems.map((e) => e.asScale(scale)),
        );
    }

    contains(point, points = true) {
        if (points) {
            for (const e of this.curves.concat(this.areas)) {
                let contains = e.contains(point, true);
                if (!!contains) return contains;
            }
        }
        for (const e of this.curves.concat(
            this.areas.sort((a, b) => area(a.poly) - area(b.poly)),
        )) {
            let contains = e.contains(point, false);
            if (!!contains) return contains;
        }
        return null;
    }

    remove(elem) {
        this.elems = this.elems.filter((e) => e !== elem);
        this.elems.map((e) => e.remove(elem));
    }

    copy(elem) {
        let name_idx = 0;
        let names = this.elems.map((a) => a.name);
        while (names.includes(`${elem.name}-copy-${name_idx}`)) {
            name_idx += 1;
        }

        let newElem = elem.copy(`${elem.name}-copy-${name_idx}`);
        this.elems.push(newElem);
        return newElem;
    }

    addDefault(type, name, props = {}) {
        const [t, b, l, r] = [
            0.1 * this.scale.y,
            0.3 * this.scale.y,
            0.1 * this.scale.x,
            0.3 * this.scale.x,
        ];
        const newElem =
            type === 'areas'
                ? new Area(
                    this.scale,
                    name,
                    props.inverted ?? false,
                    props.poly ??
                    [
                        [l, t],
                        [r, t],
                        [r, b],
                        [l, b],
                    ].map((p) => new Point(p[0], p[1], this.scale)),
                    props.ignore ?? false,
                )
                : type === 'lines'
                    ? new Curve(
                        this.scale,
                        name,
                        props.p0 ?? new Point(r, t, this.scale),
                        props.p1 ?? new Point(l, t, this.scale),
                        props.cf ?? 0,
                        props.vari ?? 5,
                    )
                    : null;
        if (newElem) this.elems.push(newElem);
        return newElem;
    }

    addFromDrawing(path) {
        let name_idx = 0;
        let names = this.elems.map((a) => a.name);

        // append the first point to the end to close the loop and run simplification
        path.push(path[0].copy());
        const points = simplify(
            path,
            3,
            15,
            (this.scale.x * this.scale.y) / 200,
            (a, b, c) => {
                return Math.abs((a.x - c.x) * (b.y - a.y) - (a.x - b.x) * (c.y - a.y));
            },
        );

        if (points.length === 3) {
            // use path directly
            while (names.includes(`line-${name_idx}`)) {
                name_idx += 1;
            }
            this.elems.push(
                new Curve(
                    this.scale,
                    `line-${name_idx}`,
                    path[path.length - 2],
                    path[0],
                    0,
                    5,
                ),
            );
        } else {
            // remove the last point as we added to close the loop
            while (names.includes(`area-${name_idx}`)) {
                name_idx += 1;
            }
            this.elems.push(
                new Area(
                    this.scale,
                    `area-${name_idx}`,
                    false,
                    points.slice(0, points.length - 1),
                    false,
                ),
            );
        }
    }

    render(ctx) {
        this.elems.forEach((elem) => {
            elem.visible ? elem.render(ctx) : null;
        });
    }

    toApi() {
        return {
            lines: this.curves.reduce(
                (curves, curve) => ({...curves, [curve.name]: curve.toApi(false)}),
                {},
            ),
            arcs: this.curves
                .filter((curve) => curve.is_arc)
                .reduce(
                    (curves, curve) => ({...curves, [curve.name]: curve.toApi(true)}),
                    {},
                ),
            areas: {
                trigger: this.areas
                    .filter((area) => !area.ignore)
                    .reduce(
                        (areas, area) => ({...areas, [area.name]: area.toApi()}),
                        {},
                    ),
                ignore: this.areas
                    .filter((area) => area.ignore)
                    .reduce(
                        (areas, area) => ({...areas, [area.name]: area.toApi()}),
                        {},
                    ),
            },
        };
    }

    static fromApi(scale, elemsDict) {
        const elems = [];
        elemsDict = elemsDict || {};

        // curves i.e. lines and arcs
        Object.entries(elemsDict.lines ?? {}).forEach(([name, value]) => {
            elems.push(
                Curve.fromApi({
                    scale: scale,
                    name: name,
                    ...(elemsDict.arcs[name] ? elemsDict.arcs[name] : value),
                }),
            );
        });

        // trigger areas
        Object.entries(elemsDict.areas?.trigger ?? {}).forEach(([name, value]) => {
            elems.push(
                Area.fromApi({
                    scale: scale,
                    name: name,
                    inverted: value.inverted,
                    poly: value.poly,
                    ignore: false,
                }),
            );
        });

        // ignore areas
        Object.entries(elemsDict.areas?.ignore ?? {}).forEach(([name, value]) => {
            elems.push(
                Area.fromApi({
                    scale: scale,
                    name: name,
                    inverted: value.inverted,
                    poly: value.poly,
                    ignore: true,
                }),
            );
        });

        return new StatElems(scale, elems);
    }
}

export {Area, Curve, Point, StatElems};
